import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Send } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t } from '../i18n';
import { Card, Button, Alert, Modal } from './ui';
import FormAnswer from './FormAnswer';
import { scoreAnswers } from '../lib/forms';

/**
 * Forms a patient's professionals asked them to answer (migration 0065), on
 * their Inicio. The patient answers and sends. The score is never shown to
 * them: only the professional who sent the form reads the answers.
 */

const AnswerForm = ({ form, onClose, onSent }) => {
  const [answers, setAnswers] = useState(() => form.questions.map(() => null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    // Only whether something is missing: the total is not the patient's to see.
    const { missing } = scoreAnswers(form.questions, [], answers);
    if (missing) { setError(t('forms.missingAnswer', { n: missing })); return; }
    setBusy(true);
    setError(null);
    const { error: submitError } = await supabase.rpc('submit_form', { p_send_id: form.send_id, p_answers: answers });
    setBusy(false);
    if (submitError) { setError(submitError.message); return; }
    onSent(t('forms.submitted', { professional: form.professional }));
  };

  return (
    <Modal
      open
      wide
      title={form.title}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={submit} loading={busy}><Send size={16} />{t('forms.submit')}</Button>
        </>
      }
    >
      <div className="space-y-4">
        {form.description && <p className="whitespace-pre-wrap text-sm text-slate-600">{form.description}</p>}
        {error && <Alert tone="danger">{error}</Alert>}
        <FormAnswer questions={form.questions} answers={answers} onChange={setAnswers} idPrefix={`send-${form.send_id}`} />
      </div>
    </Modal>
  );
};

const PendingForms = ({ showPerson = false }) => {
  const [forms, setForms] = useState([]);
  const [answering, setAnswering] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const { data, error: loadError } = await supabase.rpc('my_pending_forms');
    if (loadError) setError(loadError.message);
    setForms(data ?? []);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Nothing waiting and nothing to say: no card at all.
  if (!forms.length && !notice && !error) return null;

  return (
    <Card title={t('forms.pendingTitle')} subtitle={forms.length ? t('forms.pendingHint') : undefined}>
      <div className="space-y-3">
        {error && <Alert tone="danger">{error}</Alert>}
        {notice && <Alert tone="success">{notice}</Alert>}
        {forms.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {forms.map((form) => (
              <li key={form.send_id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">
                    {t('forms.pendingLine', { title: form.title, professional: form.professional })}
                  </p>
                  {showPerson && (
                    <p className="text-xs text-slate-500">
                      {t('forms.forPerson', { name: `${form.first_name} ${form.last_name}` })}
                    </p>
                  )}
                </div>
                <Button onClick={() => setAnswering(form)}><ClipboardList size={16} />{t('forms.answer')}</Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {answering && (
        <AnswerForm
          form={answering}
          onClose={() => setAnswering(null)}
          onSent={(message) => { setAnswering(null); setNotice(message); load(); }}
        />
      )}
    </Card>
  );
};

export default PendingForms;
